import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useRealtime } from "@/lib/ws";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, CheckCircle2, Package, XCircle, ShoppingBag } from "lucide-react";

const rupiah = (v) => "Rp" + (Math.round(v || 0)).toLocaleString("id-ID");
const STATUS_COLOR = {
  DRAFT: "bg-slate-400", APPROVED: "bg-blue-500", PARTIALLY_RECEIVED: "bg-amber-500",
  RECEIVED: "bg-emerald-500", CLOSED: "bg-emerald-600", CANCELLED: "bg-rose-500",
};

const validTabs = ["pos", "suppliers"];

export default function PurchasingPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get("tab");
  const [activeTab, setActiveTab] = useState(
    validTabs.includes(requestedTab) ? requestedTab : "pos"
  );
  useEffect(() => {
    const requested = searchParams.get("tab");
    if (validTabs.includes(requested)) setActiveTab(requested);
  }, [searchParams]);
  const changePurchasingTab = (value) => {
    if (!validTabs.includes(value)) return;
    setActiveTab(value);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set("tab", value);
      return next;
    }, { replace: true });
  };

  return (
    <div className="page-shell">
      <div data-workspace-redundant-header>
        <h1 className="text-2xl font-extrabold font-display">Purchasing</h1>
        <p className="text-slate-500 text-sm">Supplier · Purchase Order · Receiving — stok naik otomatis via PURCHASE movement</p>
      </div>
      <Tabs value={activeTab} onValueChange={changePurchasingTab} data-layered-navigation="purchasing">
        <TabsList data-global-nav-layer="2" aria-label="Navigasi Purchasing">
          <TabsTrigger value="pos">Purchase Order</TabsTrigger>
          <TabsTrigger value="suppliers">Supplier</TabsTrigger>
        </TabsList>
        <TabsContent
          value="pos"
          forceMount
          className="data-[state=inactive]:hidden"
        >
          <POTab />
        </TabsContent>
        <TabsContent
          value="suppliers"
          forceMount
          className="data-[state=inactive]:hidden"
        >
          <SupplierTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function SupplierTab() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", contact_person: "", phone: "", email: "", address: "", payment_terms: "cash" });
  const load = () => api.get("/suppliers").then(({data})=>setItems(data));
  useEffect(() => { load(); }, []);
  async function save() {
    if (!form.name) { toast.error("Nama wajib"); return; }
    try { await api.post("/suppliers", form); toast.success("Supplier ditambahkan"); setOpen(false); setForm({name:"",contact_person:"",phone:"",email:"",address:"",payment_terms:"cash"}); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  }
  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between">
        <CardTitle className="text-base">{items.length} Supplier</CardTitle>
        <Button data-testid="supplier-add" onClick={()=>setOpen(true)} size="sm" className="bg-orange-500 hover:bg-orange-600"><Plus className="w-4 h-4 mr-1"/>Tambah</Button>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader><TableRow><TableHead>Nama</TableHead><TableHead>Kontak</TableHead><TableHead>Phone</TableHead><TableHead>Terms</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
          <TableBody>
            {items.map(i => (
              <TableRow key={i.id}>
                <TableCell className="font-medium">{i.name}</TableCell>
                <TableCell className="text-xs">{i.contact_person || "-"}</TableCell>
                <TableCell className="text-xs font-mono">{i.phone || "-"}</TableCell>
                <TableCell><Badge variant="outline" className="text-[10px]">{i.payment_terms?.toUpperCase()}</Badge></TableCell>
                <TableCell>{i.active ? <Badge className="bg-emerald-500">Aktif</Badge> : <Badge variant="destructive">Non-aktif</Badge>}</TableCell>
              </TableRow>
            ))}
            {!items.length && <TableRow><TableCell colSpan={5} className="text-center py-8 text-slate-400">Belum ada supplier</TableCell></TableRow>}
          </TableBody>
        </Table>
      </CardContent>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Supplier Baru</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Nama</Label><Input data-testid="supplier-form-name" value={form.name} onChange={e=>setForm({...form, name:e.target.value})}/></div>
            <div><Label>Kontak</Label><Input value={form.contact_person} onChange={e=>setForm({...form, contact_person:e.target.value})}/></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={e=>setForm({...form, phone:e.target.value})}/></div>
            <div className="col-span-2"><Label>Alamat</Label><Input value={form.address} onChange={e=>setForm({...form, address:e.target.value})}/></div>
            <div><Label>Payment Terms</Label>
              <Select value={form.payment_terms} onValueChange={v=>setForm({...form, payment_terms:v})}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="net7">Net 7</SelectItem>
                  <SelectItem value="net14">Net 14</SelectItem>
                  <SelectItem value="net30">Net 30</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={()=>setOpen(false)}>Batal</Button><Button data-testid="supplier-form-submit" onClick={save} className="bg-orange-500 hover:bg-orange-600">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function POTab() {
  const location = useLocation();
  const navigate = useNavigate();
  const [pos, setPos] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [products, setProducts] = useState([]);
  const [docDate, setDocDate] = useState(new Date().toLocaleDateString("en-CA"));
  const [itemType, setItemType] = useState("ingredient");
  const [open, setOpen] = useState(false);
  const [receiveOpen, setReceiveOpen] = useState(null);
  const [form, setForm] = useState(() => {
    const initial = {
      supplier_id: "",
      warehouse_id: "",
      location_type: "warehouse",
      location_id: "",
      staff_name: "",
      items: [],
      tax: 0,
      notes: ""
    };

    try {
      const saved = sessionStorage.getItem("mazboy:purchasing:po-form:v1");
      if (!saved) return initial;

      const parsed = JSON.parse(saved);
      return {
        ...initial,
        ...parsed,
        items: Array.isArray(parsed.items) ? parsed.items : []
      };
    } catch {
      return initial;
    }
  });
  const [pickIng, setPickIng] = useState(""); const [pickQty, setPickQty] = useState(""); const [pickPrice, setPickPrice] = useState("");

  useEffect(() => {
    try {
      sessionStorage.setItem(
        "mazboy:purchasing:po-form:v1",
        JSON.stringify(form)
      );
    } catch {
      // Draft tetap dapat digunakan selama halaman masih terbuka.
    }
  }, [form]);

  const load = () => api.get("/purchase-orders").then(({data})=>setPos(data));
  useEffect(() => {
    api.get("/suppliers").then(({data})=>setSuppliers(data));
    api.get("/warehouses").then(({data}) => {
      const list = Array.isArray(data) ? data : [];
      setWarehouses(list);
      setForm(current => {
        if (current.location_type === "outlet") {
          return {
            ...current,
            warehouse_id: current.warehouse_id || list[0]?.id || ""
          };
        }

        const locationId = current.location_id || current.warehouse_id || list[0]?.id || "";
        return {
          ...current,
          warehouse_id: locationId,
          location_id: locationId
        };
      });
    });
    api.get("/outlets").then(({data})=>setOutlets(Array.isArray(data) ? data : []));
    api.get("/ingredients").then(({data})=>setIngredients(data));
    api.get("/products").then(({data})=>setProducts(Array.isArray(data) ? data.filter(x => x.active !== false) : []));
    load();
  }, []);
  const processedInventoryPayload = useRef(null);

  useEffect(() => {
    const incoming = location.state?.inventoryOrderItems;
    if (!Array.isArray(incoming) || incoming.length === 0) return;

    let payloadKey;
    try {
      payloadKey = JSON.stringify(incoming);
    } catch {
      return;
    }
    if (processedInventoryPayload.current === payloadKey) return;

    const invalidItem = incoming.find(
      item => !item.location_type || !item.location_id
    );
    if (invalidItem) {
      toast.error(`Lokasi tujuan belum dipilih untuk ${invalidItem.name || "item"}`);
      navigate(".", { replace: true, state: null });
      return;
    }

    processedInventoryPayload.current = payloadKey;

    setForm(current => {
      const items = [...current.items];
      incoming.forEach(item => {
        const index = items.findIndex(x =>
          x.entity === item.entity &&
          x.entity_id === item.entity_id &&
          x.location_type === item.location_type &&
          x.location_id === item.location_id
        );
        if (index >= 0) {
          const qty = Number(items[index].qty) + Number(item.qty);
          items[index] = { ...items[index], qty, subtotal: qty * Number(items[index].unit_price) };
        } else {
          items.push({
            entity: item.entity,
            entity_id: item.entity_id,
            name: item.name,
            qty: Number(item.qty),
            unit: item.unit || "pcs",
            unit_price: Number(item.unit_price) || 0,
            subtotal: Number(item.qty) * (Number(item.unit_price) || 0),
            location_type: item.location_type,
            location_id: item.location_id,
            location_name: item.location_name || item.location_id
          });
        }
      });
      return { ...current, items };
    });

    toast.success("Item Inventory dimasukkan ke form PO. Periksa data sebelum submit.");
    navigate(".", { replace: true, state: null });
  }, [location.state, navigate]);

  useRealtime((msg) => { if (msg.event?.startsWith("po.")) load(); });

  function addItem() {
    const source = itemType === "ingredient" ? ingredients : products;
    const ing = source.find(i => i.id === pickIng);
    const qty = Number(pickQty);
    const unit_price = Number(pickPrice);

    if (!ing || !pickQty || !pickPrice || !Number.isFinite(qty) || qty <= 0 ||
        !Number.isFinite(unit_price) || unit_price < 0) {
      toast.error("Pilih item, isi qty positif dan harga yang valid");
      return;
    }
    if (form.items.some(i => i.entity === itemType && i.entity_id === ing.id)) {
      toast.error("Item ini sudah ditambahkan");
      return;
    }

    setForm(f => ({
      ...f,
      items: [...f.items, {
        entity: itemType,
        entity_id: ing.id,
        name: ing.name,
        qty,
        unit: ing.unit || "pcs",
        unit_price,
        subtotal: qty * unit_price
      }]
    }));
    setPickIng("");
    setPickQty("");
    setPickPrice("");
  }

  async function createPO() {
    if (!form.items.length) {
      toast.error("Tambahkan minimal 1 item");
      return;
    }

    const groups = new Map();
    for (const item of form.items) {
      const location_type = item.location_type || form.location_type || "warehouse";
      const location_id = item.location_id || form.location_id || form.warehouse_id;
      if (!location_id) {
        toast.error(`Pilih lokasi tujuan untuk ${item.name || "item"}`);
        return;
      }
      const key = `${location_type}:${location_id}`;
      if (!groups.has(key)) groups.set(key, { location_type, location_id, items: [] });
      groups.get(key).items.push(item);
    }

    const batches = Array.from(groups.values());
    const completedKeys = new Set();

    try {
      for (const group of batches) {
        const payload = {
          ...form,
          supplier_id: form.supplier_id || null,
          location_type: group.location_type,
          location_id: group.location_id,
          warehouse_id: group.location_id,
          items: group.items.map(({ location_name, ...item }) => item),
          tax: Number(form.tax || 0) / batches.length,
        };
        await api.post("/purchase-orders", payload);
        completedKeys.add(`${group.location_type}:${group.location_id}`);
      }

      toast.success(`${batches.length} PO berhasil dibuat (status DRAFT)`);
      try {
        sessionStorage.removeItem("mazboy:purchasing:po-form:v1");
      } catch {}
      setOpen(false);
      setForm({
        supplier_id: "",
        warehouse_id: warehouses[0]?.id || "",
        location_type: "warehouse",
        location_id: warehouses[0]?.id || "",
        staff_name: "",
        items: [],
        tax: 0,
        notes: ""
      });
      load();
    } catch (e) {
      const remaining = form.items.filter(item => {
        const type = item.location_type || form.location_type || "warehouse";
        const id = item.location_id || form.location_id || form.warehouse_id;
        return !completedKeys.has(`${type}:${id}`);
      });
      setForm(f => ({ ...f, items: remaining }));
      if (completedKeys.size > 0) {
        toast.error(`${completedKeys.size} kelompok PO berhasil; kelompok berikutnya gagal. Item yang belum diproses tetap di form. Periksa daftar PO sebelum mencoba lagi.`);
        load();
      } else {
        toast.error(e?.response?.data?.detail || "Gagal membuat PO");
      }
    }
  }

  async function approve(pid) {
    try { await api.post(`/purchase-orders/${pid}/approve`); toast.success("PO approved"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  }

  async function submitReceive(po) {
    const items = po.items.map(it => ({
      entity_id: it.entity_id,
      received_qty: Number(document.querySelector(`[data-po-recv="${it.entity_id}"]`)?.value || 0),
    })).filter(i => i.received_qty > 0);
    if (!items.length) { toast.error("Isi minimal 1 qty diterima"); return; }
    try {
      await api.post(`/purchase-orders/${po.id}/receive`, { items, notes: "" });
      toast.success("Barang diterima, stok bertambah");
      setReceiveOpen(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  }

  const supplierName = (id) => suppliers.find(s=>s.id===id)?.name || "-";

  function getExportGroups() {
    const groups = new Map();
    for (const item of form.items) {
      const location_type = item.location_type || form.location_type || "warehouse";
      const location_id = item.location_id || form.location_id || form.warehouse_id;
      if (!location_id) {
        toast.error(`Pilih lokasi tujuan untuk ${item.name || "item"}`);
        return null;
      }
      const key = `${location_type}:${location_id}`;
      if (!groups.has(key)) groups.set(key, { location_type, location_id, items: [] });
      groups.get(key).items.push(item);
    }
    return Array.from(groups.values()).map(group => {
      const locations = group.location_type === "outlet" ? outlets : warehouses;
      return {
        ...group,
        location_name: locations.find(x => x.id === group.location_id)?.name || group.location_id,
        subtotal: group.items.reduce((sum, item) => sum + Number(item.subtotal || 0), 0),
      };
    });
  }

  function downloadCSV() {
    if (!form.items.length) { toast.error("Tambahkan item sebelum ekspor"); return; }
    const groups = getExportGroups();
    if (!groups) return;
    groups.forEach((group, index) => {
      const rows = [
        ["Tanggal", "Supplier", "Lokasi Tujuan", "Nama Petugas", "Jenis Item", "Nama Item", "Qty", "Satuan", "Harga Satuan", "Subtotal", "Pajak", "Total Nilai PO", "Catatan"],
        ...group.items.map(i => [
          docDate, supplierName(form.supplier_id), group.location_name,
          form.staff_name, i.entity, i.name, i.qty, i.unit, i.unit_price,
          i.subtotal, 0, group.subtotal, form.notes
        ])
      ];
      const csv = rows.map(row => row.map(v => '"' + String(v ?? "").replace(/"/g, '""') + '"').join(",")).join("\r\n");
      const url = URL.createObjectURL(new Blob(["\uFEFF" + csv], {type: "text/csv;charset=utf-8"}));
      const a = document.createElement("a");
      a.href = url;
      a.download = `purchase-order-${group.location_type}-${String(group.location_name).replace(/[^a-zA-Z0-9_-]+/g, "-")}${groups.length > 1 ? `-${index + 1}` : ""}.csv`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    });
  }

  function downloadPNG() {
    if (!form.items.length) { toast.error("Tambahkan item sebelum ekspor"); return; }
    const groups = getExportGroups();
    if (!groups) return;
    groups.forEach((group, index) => {
      const width = 1000, rowHeight = 36, height = 340 + group.items.length * rowHeight;
      const canvas = document.createElement("canvas");
      canvas.width = width; canvas.height = height;
      const ctx = canvas.getContext("2d");
      if (!ctx) { toast.error("Browser tidak mendukung ekspor PNG"); return; }
      ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, width, height);
      ctx.fillStyle = "#111827";
      ctx.font = "bold 28px Arial"; ctx.fillText("MAZBOY HUB — PURCHASE ORDER", 32, 44);
      ctx.font = "17px Arial";
      ctx.fillText("Tanggal: " + docDate, 32, 82);
      ctx.fillText("Supplier: " + supplierName(form.supplier_id), 32, 112);
      ctx.fillText("Lokasi: " + group.location_name, 32, 142);
      ctx.fillText("Petugas: " + (form.staff_name || "-"), 32, 172);
      ctx.fillText("Status: Draft (belum disubmit)", 32, 202);
      ctx.font = "bold 15px Arial";
      ctx.fillText("ITEM", 32, 246); ctx.fillText("QTY", 570, 246);
      ctx.fillText("SATUAN", 650, 246); ctx.fillText("HARGA", 760, 246);
      ctx.fillText("SUBTOTAL", 870, 246);
      ctx.font = "14px Arial";
      group.items.forEach((it, i) => {
        const y = 276 + i * rowHeight;
        ctx.fillText((i + 1) + ". " + String(it.name).slice(0, 48), 32, y);
        ctx.fillText(String(it.qty), 570, y);
        ctx.fillText(String(it.unit || "-").slice(0, 12), 650, y);
        ctx.fillText(rupiah(it.unit_price), 760, y);
        ctx.fillText(rupiah(it.subtotal), 870, y);
      });
      ctx.font = "bold 16px Arial";
      ctx.fillText("Subtotal: " + rupiah(group.subtotal), 32, height - 78);
      ctx.fillText("Pajak: Rp0", 32, height - 52);
      ctx.fillText("TOTAL NILAI PO: " + rupiah(group.subtotal), 500, height - 52);
      ctx.font = "14px Arial";
      ctx.fillText("Catatan: " + (form.notes || "-").slice(0, 100), 32, height - 22);
      canvas.toBlob(blob => {
        if (!blob) { toast.error("Gagal membuat PNG"); return; }
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `purchase-order-${group.location_type}-${String(group.location_name).replace(/[^a-zA-Z0-9_-]+/g, "-")}${groups.length > 1 ? `-${index + 1}` : ""}.png`;
        a.click();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      }, "image/png");
    });
  }

  const subtotalForm = form.items.reduce((s, i) => s + i.subtotal, 0);

  return (
    <>
      <Card className="mb-6">
        <CardHeader><CardTitle className="text-base">Form Purchase Order</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Tanggal Dokumen</Label><Input type="date" value={docDate} onChange={e=>setDocDate(e.target.value)}/></div>
            <div><Label>Supplier (opsional)</Label>
              <Select value={form.supplier_id || "none"} onValueChange={v=>setForm({...form, supplier_id:v === "none" ? "" : v})}>
                <SelectTrigger data-testid="po-form-supplier"><SelectValue placeholder="Tanpa supplier"/></SelectTrigger>
                <SelectContent><SelectItem value="none">Tanpa supplier</SelectItem>{suppliers.map(s=><SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Jenis Lokasi Tujuan</Label>
              <Select value={form.location_type} onValueChange={v=>{const list=v === "outlet" ? outlets : warehouses; const id=list[0]?.id || ""; setForm(f=>({...f, location_type:v, location_id:id, warehouse_id:id}));}}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent><SelectItem value="warehouse">Gudang</SelectItem><SelectItem value="outlet">Outlet</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Lokasi Tujuan</Label>
              <Select value={form.location_id || form.warehouse_id} onValueChange={v=>setForm(f=>({...f, location_id:v, warehouse_id:v}))}>
                <SelectTrigger><SelectValue placeholder="Pilih lokasi tujuan"/></SelectTrigger>
                <SelectContent>{(form.location_type === "outlet" ? outlets : warehouses).map(x=><SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Nama Petugas</Label>
              <Input value={form.staff_name} onChange={e=>setForm({...form, staff_name:e.target.value})} placeholder="Nama petugas PO"/>
            </div>
          </div>
          <div className="border rounded-lg p-3 space-y-2">
            <div className="flex flex-wrap gap-2 mb-3">
              <Button type="button" size="sm" variant={itemType === "ingredient" ? "default" : "outline"} onClick={()=>{setItemType("ingredient");setPickIng("");}}>Tambah Bahan</Button>
              <Button type="button" size="sm" variant={itemType === "product" ? "default" : "outline"} onClick={()=>{setItemType("product");setPickIng("");}}>Tambah Produk</Button>
            </div>
            <div className="grid grid-cols-[1fr_80px_120px_auto] gap-2 items-end">
              <div>
                <Label className="text-xs">{itemType === "ingredient" ? "Bahan" : "Produk"}</Label>
                <Select value={pickIng} onValueChange={v=>{setPickIng(v); const source=itemType === "ingredient" ? ingredients : products; const selected=source.find(x=>x.id===v); const price=itemType === "ingredient" ? selected?.cost : selected?.hpp; setPickPrice(String(price ?? 0));}}>
                  <SelectTrigger data-testid="po-pick-ing"><SelectValue placeholder={itemType === "ingredient" ? "Pilih bahan..." : "Pilih produk..."}/></SelectTrigger>
                  <SelectContent>{(itemType === "ingredient" ? ingredients : products).map(i=><SelectItem key={i.id} value={i.id}>{i.name} ({i.unit || "pcs"})</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Qty</Label><Input data-testid="po-pick-qty" type="number" value={pickQty} onChange={e=>setPickQty(e.target.value)}/></div>
              <div><Label className="text-xs">Harga/unit</Label><Input data-testid="po-pick-price" type="number" value={pickPrice} onChange={e=>setPickPrice(e.target.value)}/></div>
              <Button data-testid="po-add-item" onClick={addItem} size="sm">Tambah</Button>
            </div>
            {form.items.length > 0 && (
              <div className="text-xs space-y-1 pt-2 border-t">
                {form.items.map((it, i) => (
                  <div key={it.entity + it.entity_id} className="grid grid-cols-[1fr_90px_100px_auto] gap-2 items-center p-2 bg-slate-50 rounded">
                    <span className="text-xs">{it.name}<br/>{it.entity} · {rupiah(it.unit_price)} / {it.unit}</span>
                    <Input aria-label={"Qty " + it.name} type="number" min="0.01" step="0.01" value={it.qty}
                      onChange={e=>{const qty=Number(e.target.value);setForm(f=>({...f,items:f.items.map((v,j)=>j===i?{...v,qty,subtotal:qty*v.unit_price}:v)}));}}/>
                    <b className="text-xs">{rupiah(it.subtotal)}</b>
                    <Button type="button" size="icon" variant="ghost" onClick={()=>setForm(f=>({...f,items:f.items.filter((_,j)=>j!==i)}))}><XCircle className="w-4 h-4 text-rose-500"/></Button>
                  </div>
                ))}
                <div className="flex justify-between font-semibold pt-2 border-t">
                  <span>Subtotal</span><span className="num">{rupiah(subtotalForm)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Pajak</span><span className="num">{rupiah(Number(form.tax || 0))}</span>
                </div>
                <div className="flex justify-between font-bold text-base pt-2 border-t text-orange-600">
                  <span>Total Nilai PO</span><span className="num">{rupiah(subtotalForm + Number(form.tax || 0))}</span>
                </div>
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Pajak (Rp)</Label><Input type="number" value={form.tax} onChange={e=>setForm({...form, tax:e.target.value})}/></div>
            <div><Label>Catatan</Label><Input value={form.notes} onChange={e=>setForm({...form, notes:e.target.value})}/></div>
          </div>
          <div className="flex flex-wrap justify-end gap-2">
            <Button type="button" variant="outline" onClick={downloadCSV}>Download CSV</Button>
            <Button type="button" variant="outline" onClick={downloadPNG}>Download PNG</Button>
            <Button data-testid="po-form-submit" onClick={createPO} className="bg-orange-500 hover:bg-orange-600">Submit Purchase Order</Button>
          </div>
        </CardContent>
      </Card>


      <Card>
        <CardHeader className="flex-row items-center justify-between">
          <CardTitle className="text-base">{pos.length} Purchase Order</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader><TableRow>
              <TableHead>No. PO</TableHead><TableHead>Supplier</TableHead><TableHead>Item</TableHead>
              <TableHead className="text-right">Total</TableHead><TableHead>Status</TableHead>
              <TableHead>Dibuat</TableHead><TableHead className="text-right">Aksi</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {pos.map(p => (
                <TableRow key={p.id}>
                  <TableCell className="font-mono text-xs">{p.po_no}</TableCell>
                  <TableCell>{supplierName(p.supplier_id)}</TableCell>
                  <TableCell className="text-xs">{p.items.length} item</TableCell>
                  <TableCell className="text-right num font-semibold">{rupiah(p.total)}</TableCell>
                  <TableCell><Badge className={STATUS_COLOR[p.status]}>{p.status.replace("_", " ")}</Badge></TableCell>
                  <TableCell className="text-xs">{new Date(p.created_at).toLocaleString("id-ID")}</TableCell>
                  <TableCell className="text-right space-x-1">
                    {p.status === "DRAFT" && (
                      <Button data-testid={`po-approve-${p.id}`} size="sm" onClick={()=>approve(p.id)} className="h-7 px-2 bg-blue-500 hover:bg-blue-600"><CheckCircle2 className="w-3 h-3 mr-1"/>Approve</Button>
                    )}
                    {["APPROVED", "PARTIALLY_RECEIVED"].includes(p.status) && (
                      <Button data-testid={`po-receive-${p.id}`} size="sm" onClick={()=>setReceiveOpen(p)} className="h-7 px-2 bg-emerald-500 hover:bg-emerald-600"><Package className="w-3 h-3 mr-1"/>Terima</Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {!pos.length && <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-400">Belum ada PO</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={!!receiveOpen} onOpenChange={(o)=>!o && setReceiveOpen(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Terima Barang · {receiveOpen?.po_no}</DialogTitle></DialogHeader>
          <div className="text-xs text-slate-500 mb-2">Isi qty yang benar-benar diterima. Bisa partial (sebagian). Sisa dapat diterima di batch berikutnya.</div>
          <div className="space-y-2 max-h-96 overflow-auto">
            {receiveOpen?.items.map(it => {
              const alreadyRecv = receiveOpen.received_qty?.[it.entity_id] || 0;
              const remaining = it.qty - alreadyRecv;
              return (
                <div key={it.entity_id} className="grid grid-cols-[1fr_100px_100px] gap-2 p-2 bg-slate-50 rounded items-center">
                  <div className="text-sm">
                    <div className="font-medium">{it.name}</div>
                    <div className="text-xs text-slate-500">Order: {it.qty} · Sudah diterima: {alreadyRecv} · Sisa: {remaining}</div>
                  </div>
                  <Input data-po-recv={it.entity_id} type="number" step="0.01" defaultValue={remaining} placeholder="Terima"/>
                  <div className="text-xs text-slate-500 text-right">{it.unit}</div>
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setReceiveOpen(null)}>Batal</Button>
            <Button data-testid="po-receive-submit" onClick={()=>submitReceive(receiveOpen)} className="bg-emerald-500 hover:bg-emerald-600">Konfirmasi Terima</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
